import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import logger from '../utility/logger';
import { reloadPage } from '../utility/navigation';
import { baseQueryWithTimeout } from './queries/base/queryWithTimeout';

const baseQuery = fetchBaseQuery({
    prepareHeaders: (headers) => {
        if (!headers.has('X-Requested-With')) {
            headers.set('X-Requested-With', 'XMLHttpRequest');
        }
        return headers;
    } });

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    object,
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    if (result.error) {
        logger.error(`Error found in ${args} : ${api.endpoint}`, {
            error: result.error,
        }, false);
        if (result.error.status === 401) {
            // This reload was previously invisible in telemetry: the generic error log above
            // opts out of Datadog, and the reload destroys the page before anything else can be
            // recorded. To the viewer it reads as being "kicked out". Sending this action to
            // Datadog (third argument true) is what lets that complaint class finally be counted
            // against a mechanism.
            logger.error('Session rejected by the API, reloading the page', {
                endpoint: api.endpoint,
            }, true);
            reloadPage();
        }
    } 
    return result;
};

const baseQueryWithTimeoutAndReauth = baseQueryWithTimeout(
    '',
    baseQueryWithReauth,
);

// Define our single API slice object
export const apiSlice = createApi({
    // The cache reducer expects to be added at `state.api` (already default - this is optional)
    reducerPath: 'api',
    tagTypes: ['Licenses', 'Bookmarks', 'CleTimeRecords', 'CleSession'],
    baseQuery: baseQueryWithTimeoutAndReauth,
    endpoints: () => ({ })
});
